// ============================================================
// VISA FORM - CONSTANTS
// ============================================================

// ----------------------------------------------------------------
// Pagination constants (used across all sections)
// ----------------------------------------------------------------
export const DESKTOP_INITIAL = 6;
export const MOBILE_INITIAL = 3;

export function getInitialVisible() {
  if (typeof window !== "undefined" && window.innerWidth <= 767) return MOBILE_INITIAL;
  return DESKTOP_INITIAL;
}

// ----------------------------------------------------------------
// Country & State Search Aliases
// Short-forms / common names  full official names used in data
// ----------------------------------------------------------------
export const COUNTRY_ALIASES = {
  usa: "United States of America",
  us: "United States of America",
  america: "United States of America",
  "united states": "United States of America",
  uk: "United Kingdom",
  gb: "United Kingdom",
  britain: "United Kingdom",
  england: "United Kingdom",
  "great britain": "United Kingdom",
  uae: "UAE",
  dubai: "UAE",
  emirates: "UAE",
  "united arab": "UAE",
  aus: "Australia",
  nz: "New Zealand",
  sg: "Singapore",
  sing: "Singapore",
  jp: "Japan",
  jpn: "Japan",
  korea: "South Korea",
  kr: "South Korea",
  de: "Germany",
  ger: "Germany",
  deutsch: "Germany",
  fr: "France",
  fra: "France",
  ca: "Canada",
  can: "Canada",
  nl: "Netherlands",
  neth: "Netherlands",
  holland: "Netherlands",
  it: "Italy",
  ita: "Italy",
  ch: "Switzerland",
  swiss: "Switzerland",
  switz: "Switzerland",
  my: "Malaysia",
  mal: "Malaysia",
  th: "Thailand",
  thai: "Thailand",
  vn: "Vietnam",
  viet: "Vietnam",
  lk: "Sri Lanka",
  ie: "Ireland",
  pt: "Portugal",
  por: "Portugal",
  es: "Spain",
  spa: "Spain",
  cn: "China",
  chn: "China",
  prc: "China",
};

export const STATE_ALIASES = {
  dl: "Delhi",
  del: "Delhi",
  nd: "Delhi",
  mh: "Maharashtra",
  mum: "Maharashtra",
  mumbai: "Maharashtra",
  ka: "Karnataka",
  blr: "Karnataka",
  bangalore: "Karnataka",
  bengaluru: "Karnataka",
  tn: "Tamil Nadu",
  chennai: "Tamil Nadu",
  ts: "Telangana",
  hyd: "Telangana",
  hyderabad: "Telangana",
  kl: "Kerala",
  gj: "Gujarat",
  goa: "Goa",
  wb: "West Bengal",
  kolkata: "West Bengal",
  ap: "Andhra Pradesh",
  up: "Uttar Pradesh",
  rj: "Rajasthan",
  pb: "Punjab",
  hr: "Haryana",
  hp: "Himachal Pradesh",
  mp: "Madhya Pradesh",
  or: "Odisha",
  od: "Odisha",
  ch: "Chandigarh",
  as: "Assam",
};

export function matchesCountrySearch(countryName, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!countryName) return false;
  const name = countryName.toLowerCase();
  if (name.includes(q)) return true;
  const expanded = COUNTRY_ALIASES[q];
  if (expanded && name.includes(expanded.toLowerCase())) return true;
  if (q.length >= 2) {
    for (const [alias, full] of Object.entries(COUNTRY_ALIASES)) {
      if (alias.startsWith(q) && name.includes(full.toLowerCase())) return true;
    }
  }
  return false;
}

export function matchesStateSearch(stateName, query) {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  if (!stateName) return false;
  const name = stateName.toLowerCase();
  if (name.includes(q)) return true;
  const expanded = STATE_ALIASES[q];
  if (expanded && name.includes(expanded.toLowerCase())) return true;
  if (q.length >= 2) {
    for (const [alias, full] of Object.entries(STATE_ALIASES)) {
      if (alias.startsWith(q) && name.includes(full.toLowerCase())) return true;
    }
  }
  return false;
}

// ----------------------------------------------------------------
// Contact Modal Text Constants
// ----------------------------------------------------------------
export const CONTACT_MODAL_TEXT = {
  title: "Can't find what you're looking for?",
  subtitle: (sectionName) =>
    `Your search in ${sectionName} didn't match any results. Let us know what you need — our team will help you find the right information.`,
  queryPreview: (query) => `Your search: "${query}"`,
  form: {
    nameLabel: "Your Name *",
    namePlaceholder: "e.g. Arjun Sharma",
    emailLabel: "Email Address *",
    emailPlaceholder: "e.g. arjun@email.com",
    messageLabel: "What are you looking for? *",
    messagePlaceholder: (sectionName) =>
      `Describe the ${sectionName.toLowerCase()} information you need...`,
    submit: "Send Enquiry",
  },
  success: {
    title: "Enquiry Sent!",
    text: (name, sectionName, email) =>
      `Thank you, ${name}. We've received your request regarding ${sectionName} and will get back to you at ${email} shortly.`,
  },
  close: "Close",
  closeAriaLabel: "Close",
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "The Offices", href: "#visa-offices" },
  { label: "Embassies", href: "#embassies" },
  { label: "Visa Form", href: "#visa-forms" },
];

// ----------------------------------------------------------------
// Resources Section Data
// ----------------------------------------------------------------
export const RESOURCES_DATA = [
  {
    id: "official",
    title: "Official Government Portals",
    links: [
      { label: "E-FRRO India Portal", href: "https://indianfrro.gov.in", desc: "Foreigners Regional Registration" },
      { label: "Passport Seva", href: "https://passportindia.gov.in", desc: "Apply & track passports" },
      { label: "Indian Visa Online", href: "https://indianvisaonline.gov.in", desc: "Online visa applications" },
      { label: "Ministry of External Affairs", href: "https://www.mea.gov.in", desc: "Foreign policy & services" },
      { label: "Ministry of Home Affairs", href: "https://www.mha.gov.in", desc: "Immigration regulations" },
    ],
  },
  {
    id: "support",
    title: "Helplines & Support",
    links: [
      { label: "FRRO Helpdesk", href: "https://indianfrro.gov.in", desc: "Foreigners registration support" },
      { label: "Passport Helpline: 1800-258-1800", href: "https://passportindia.gov.in", desc: "Toll-free passport queries" },
      { label: "Consular Services", href: "https://www.mea.gov.in/consular-services.htm", desc: "Embassy & consulate help" },
      { label: "e-FRRO Portal", href: "https://indianfrro.gov.in/eservices/", desc: "Online FRRO services" },
    ],
  },

];

